/*
============================================================================
V600.5 HOME-MATCHED START DRIVE HANDOFF
- Uses the exact same navigation method as the working Home Start Drive card.
- window.location.href = "/pages/start-drive" after 80 ms.
- Preserves the Adventure destination payload before navigation.
============================================================================
*/

/* DRIVE IQ — ADVENTURE MODE V600.5 HOME-MATCHED START DRIVE HANDOFF
   File: adventure-route.js

   Purpose:
   - The only Adventure → Drive Mode handoff authority.
   - Preserves exact destination identity.
   - Writes the exact payload Drive Mode V215 listens for.
   - Opens Start Drive after persistence so the existing route builder creates geometry.
   - Avoids the broken direct Drive Mode URL path that can fall back to the app home page.
*/

(function (window) {
  "use strict";

  if (window.DriveIQAdventureRoute) {
    return;
  }

  var config = window.DriveIQAdventureConfig;
  var utils = window.DriveIQAdventureUtils;
  var state = window.DriveIQAdventureState;
  var events = window.DriveIQAdventureEvents;

  if (!config || !utils || !state || !events) {
    throw new Error(
      "adventure-route.js requires config, utils, state, and events first."
    );
  }

  var initialized = false;
  var launching = false;
  var unsubscribeRouteRequest = null;

  function validCoordinate(item) {
    return !!(
      item &&
      isFinite(Number(item.lat)) &&
      isFinite(Number(item.lng)) &&
      Math.abs(Number(item.lat)) <= 90 &&
      Math.abs(Number(item.lng)) <= 180
    );
  }

  function destinationID(item) {
    return String(
      item.placeId ||
      item.place_id ||
      item.googlePlaceId ||
      item.id ||
      utils.uuid()
    );
  }

  function buildDestination(item) {
    var id = destinationID(item);

    return {
      id: id,
      placeId: String(
        item.placeId ||
        item.place_id ||
        item.googlePlaceId ||
        ""
      ),
      name: String(item.label || item.name || "Destination"),
      label: String(item.label || item.name || "Destination"),
      address: String(
        item.address ||
        item.formattedAddress ||
        item.full ||
        ""
      ),
      full: String(
        item.full ||
        item.formattedAddress ||
        item.address ||
        ""
      ),
      city: String(item.city || ""),
      state: String(item.state || ""),
      category: String(
        item.userFacingCategory ||
        item.category ||
        item.primaryCategory ||
        item.poiClass ||
        ""
      ),
      lat: Number(item.lat),
      lng: Number(item.lng),
      source: "adventure-place-card",
      sourceMode: "adventure",
      exactIdentity: true
    };
  }

  function buildPayload(item) {
    var timestamp = Date.now();
    var destination = buildDestination(item);
    var handoffId =
      "adventure_" +
      String(timestamp) +
      "_" +
      String(destination.placeId || destination.id);

    return {
      handoffId: handoffId,
      requestId: handoffId,
      id: destination.id,
      placeId: destination.placeId,
      name: destination.name,
      label: destination.label,
      address: destination.address,
      full: destination.full,
      city: destination.city,
      state: destination.state,
      category: destination.category,
      lat: destination.lat,
      lng: destination.lng,

      /*
       * Keep both flat and nested destination shapes.
       * Drive Mode V215 accepts either, while older consumers remain compatible.
       */
      destination: destination,

      source: "adventure-place-card",
      sourceMode: "adventure",
      createdAt: timestamp,
      committedAt: timestamp,
      exactIdentity: true,
      handoffConsumed: false
    };
  }

  function persist(payload) {
    var storage = config.storage;
    var activeDriveID = payload.handoffId || payload.id || utils.uuid();

    /*
     * Destination and handoff payload are committed before navigation.
     * Remove stale route geometry/intel so Drive Mode cannot hydrate the old line
     * while the fresh Adventure route is being requested.
     */
    if (storage.routeGeo) {
      utils.lsRemove(storage.routeGeo);
    } else {
      utils.lsRemove("diq_route_geo_v1");
    }

    if (storage.routeIntel) {
      utils.lsRemove(storage.routeIntel);
    } else {
      utils.lsRemove("diq_route_intel_v1");
    }

    utils.lsSet(
      storage.destination,
      JSON.stringify(payload.destination)
    );

    utils.lsSet(
      storage.drivePayload,
      JSON.stringify(payload)
    );

    utils.lsSet(
      storage.driveOn,
      "1"
    );

    utils.lsSet(
      storage.activeDriveID,
      activeDriveID
    );

    /*
     * Clear prior consumption/failure markers so this exact selection must build.
     */
    utils.lsRemove("diq_drive_mode_adventure_consumed_v1");
    utils.lsRemove("diq_drive_mode_adventure_failure_v1");
    utils.lsRemove("diq_route_handoff_consumed_v1");
    utils.lsRemove("diq_route_handoff_failure_v1");

    return activeDriveID;
  }

  function launch(payload) {
    launching = true;

    /*
     * V600.5:
     * Match the proven Home page Start Drive navigation exactly.
     * Home uses window.location.href = "/pages/start-drive" after 80 ms.
     * No native bridge, no location.assign(), no absolute-domain rewrite.
     */
    window.setTimeout(function () {
      window.location.href = "/pages/start-drive";
    }, 80);
  }

  function fail(message, item) {
    var detail = {
      message: String(message || "Route could not start"),
      item: item || null,
      timestamp: Date.now()
    };

    launching = false;

    state.merge("route", {
      active: false,
      requested: false,
      error: detail.message
    }, {
      action: "route:failed"
    });

    events.emit("route:failed", detail);

    return false;
  }

  function startRouteTo(item) {
    var payload;
    var activeDriveID;

    if (launching) {
      return false;
    }

    if (!item) {
      return fail("No place is selected.", item);
    }

    if (!validCoordinate(item)) {
      return fail(
        "This place does not have trustworthy route coordinates.",
        item
      );
    }

    payload = buildPayload(item);
    activeDriveID = persist(payload);

    state.transaction("route:start", function () {
      state.setDestination(
        item,
        "route-request",
        true
      );

      state.merge("placeCard", {
        routeRequested: true
      }, {
        action: "route:place-card-committed"
      });

      state.merge("route", {
        active: true,
        requested: false,
        selectedPlace: item,
        payload: payload,
        geometry: null,
        requestedAt: Date.now(),
        committedAt: Date.now(),
        launchStartedAt: Date.now(),
        error: null
      }, {
        action: "route:start"
      });
    });

    events.emit("route:started", {
      item: item,
      payload: payload,
      activeDriveID: activeDriveID
    });

    launch(payload);

    return true;
  }

  function cancel() {
    launching = false;

    state.merge("route", {
      active: false,
      requested: false,
      launchStartedAt: 0,
      error: null
    }, {
      action: "route:cancel"
    });

    events.emit("route:cancelled", {});

    return true;
  }

  function routeItemFromEvent(event) {
    var payload = event && event.payload
      ? event.payload
      : event;

    if (!payload) {
      return null;
    }

    return (
      payload.item ||
      payload.place ||
      payload.destination ||
      payload.selectedPlace ||
      payload.result ||
      payload
    );
  }

  function init() {
    if (initialized) {
      return true;
    }

    unsubscribeRouteRequest = events.on(
      "route:requested",
      function (event) {
        startRouteTo(routeItemFromEvent(event));
      }
    );

    initialized = true;

    return true;
  }

  function destroy() {
    if (unsubscribeRouteRequest) {
      unsubscribeRouteRequest();
      unsubscribeRouteRequest = null;
    }

    initialized = false;
    launching = false;

    return true;
  }

  window.DriveIQAdventureRoute = {
    init: init,
    destroy: destroy,
    startRouteTo: startRouteTo,
    cancel: cancel,
    buildPayload: buildPayload
  };

  init();

  if (
    config.logging.enabled &&
    window.console &&
    window.console.log
  ) {
    window.console.log(
      config.logging.prefix,
      "Route loaded",
      "V600.5_HOME_MATCHED_START_DRIVE_HANDOFF"
    );
  }
}(window));
